import React, { memo, useEffect, useLayoutEffect, useState } from "react";

import AppPurchaseDisabled from "shared/components/atoms/AppPurchaseDisabled";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";

import { EnrollmentItemsProps } from "./@types/enrollments";
import ItemsToAccessContent from "./components/ItemsToAccessContent";
import ItemsToBuyBundles from "./components/ItemsToBuyBundles";
import ItemsToBuyIndividuallyOrByPlans from "./components/ItemsToBuyIndividuallyOrByPlans";
import ItemsToCourseAuthor from "./components/ItemsToCourseAuthor";
import ItemsToStartEnrollment from "./components/ItemsToStartEnrollment";
import ItemsToWarnThatIsPrivate from "./components/ItemsToWarnThatIsPrivate";
import { Container } from "./styles";

const EnrollmentItems = ({
  entity,
  context,
  isContentScreen = false,
  isProductBundleScreen = false,
  isCourseLandingPageCard = false,
  isCourseLandingPageFooter = false,
}: EnrollmentItemsProps) => {
  const { loggedUser } = useAuthContext();
  const { state, setState } = useGlobalContext();
  const [returnRoute, setReturnRoute] = useState("/");
  const [publishedBundles, setPublishedBundles] = useState([]);

  const themeBuyButtonsOption =
    state.tenancy?.settings?.["advanced-theme"]?.themeBuyButtonsOption;

  const themeBuyButtonsColor =
    state.tenancy?.settings?.["advanced-theme"]?.themeBuyButtonsColor;

  const isAuthorOrAdmin = loggedUser?.admin || entity?.is_author;

  const isHasEnrollmentAndDontStartEnrollment =
    (entity?.has_enrollment || entity?.has_access_in_other_bundle) &&
    !entity.start_enrollment;

  const isNotContentScreenAndHasEnrollment =
    !isContentScreen && entity?.has_enrollment && entity.start_enrollment;

  const isPrivateWithoutEnrollment =
    entity?.access_type === "private" && !entity?.has_enrollment;

  const hasBundlesWithoutEnrollment =
    entity?.access_type !== "private" &&
    publishedBundles?.length > 0 &&
    !entity?.has_enrollment;

  const isPaidAndDontHasEnrollment =
    !entity?.has_enrollment && entity?.access_type === "paid";

  useEffect(() => {
    switch (context) {
      case "course":
        setReturnRoute("/courses");
        break;

      case "media_space_content":
        setReturnRoute("/media");
        break;

      default:
        break;
    }
  }, [context]);

  useEffect(() => {
    if (entity?.product_bundles?.data?.length > 0) {
      const visibleBundles = entity.product_bundles.data?.filter(
        (bundle) =>
          (bundle?.status === "published" &&
            bundle?.access_type !== "private" &&
            bundle?.individual_sale) ||
          bundle?.plans.filter((plan) => plan.status === "published").length,
      );

      setPublishedBundles(visibleBundles);
    }
  }, [entity]);

  useLayoutEffect(
    () => () => {
      if (state?.enrollments?.paymentOptions?.show) {
        setState((prev) => ({
          ...prev,
          enrollments: {
            paymentOptions: {
              entity: "",
              context: "",
              show: false,
            },
          },
        }));
      }
    },
    [],
  );

  return (
    <>
      {entity && (
        <>
          <Container
            isProductBundleScreen={isProductBundleScreen}
            isCourseLandingPageCard={isCourseLandingPageCard}
            isCourseLandingPageFooter={isCourseLandingPageFooter}
            customButtonColor={themeBuyButtonsColor}
            themeBuyButtonsOption={themeBuyButtonsOption}
          >
            {isAuthorOrAdmin ? (
              <ItemsToCourseAuthor entity={entity} />
            ) : isHasEnrollmentAndDontStartEnrollment ? (
              <ItemsToStartEnrollment entity={entity} context={context} />
            ) : isNotContentScreenAndHasEnrollment ? (
              <ItemsToAccessContent
                context={context}
                entity={entity}
                isProductBundleScreen={isProductBundleScreen}
              />
            ) : state?.isMobileAppClient ? (
              <AppPurchaseDisabled />
            ) : isPrivateWithoutEnrollment ? (
              <ItemsToWarnThatIsPrivate
                returnRoute={returnRoute}
                isContentScreen={isContentScreen}
              />
            ) : hasBundlesWithoutEnrollment ? (
              <ItemsToBuyBundles
                entity={entity}
                context={context}
                bundles={publishedBundles}
                isContentScreen={isContentScreen}
                isProductBundleScreen={isProductBundleScreen}
                isCourseLandingPageCard={isCourseLandingPageCard}
                isCourseLandingPageFooter={isCourseLandingPageFooter}
              />
            ) : (
              isPaidAndDontHasEnrollment && (
                <ItemsToBuyIndividuallyOrByPlans
                  entity={entity}
                  context={context}
                  isContentScreen={isContentScreen}
                  isProductBundleScreen={isProductBundleScreen}
                  isCourseLandingPageCard={isCourseLandingPageCard}
                  isCourseLandingPageFooter={isCourseLandingPageFooter}
                />
              )
            )}
          </Container>
        </>
      )}
    </>
  );
};

export default memo(EnrollmentItems);
